import React, { useEffect, useRef, useState } from 'react';

/**
 * Sign-ins hour by hour over the last 24 hours (analytics dashboard `last24h`):
 * one series, so no legend — the card's title names it. Columns grow from one
 * baseline with a 4px rounded cap; each hour is a hover / keyboard target with
 * its own readout, the busiest hour is labelled on its cap, and the same numbers
 * are one click away as a table.
 */

const HEIGHT = 220;
const PAD = { top: 18, right: 8, bottom: 26, left: 34 };
// The navy theme's data-mark blue (styles/sb-admin.css palette note).
const BAR = '#2f5d9e';

const hourLabel = (h) => `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;
// 1, 2, 5 × 10ⁿ — clean axis tops.
const niceCeil = (v) => {
    const p = 10 ** Math.floor(Math.log10(v));
    const n = v / p;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
};
const compact = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(n));

export default function SignInsChart({ buckets = [], formatBytes }) {
    const wrapRef = useRef(null);
    const [width, setWidth] = useState(720);
    const [active, setActive] = useState(null);
    const [asTable, setAsTable] = useState(false);

    // Drawn at the card's real width, so text stays its own size on a phone.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.round(entry.contentRect.width))));
        ro.observe(el);
        return () => ro.disconnect();
    }, [asTable]);

    const total = buckets.reduce((n, b) => n + b.sessions, 0);
    const peak = buckets.reduce((best, b, i) => (b.sessions > (buckets[best]?.sessions ?? -1) ? i : best), 0);
    const top = niceCeil(Math.max(1, buckets[peak]?.sessions || 0));
    const ticks = top % 2 === 0 ? [0, top / 2, top] : [0, top];

    const plotW = width - PAD.left - PAD.right;
    const plotH = HEIGHT - PAD.top - PAD.bottom;
    const base = PAD.top + plotH;
    const slot = plotW / Math.max(1, buckets.length);
    const barW = Math.max(2, Math.min(24, slot - 4));
    const yOf = (v) => base - (v / top) * plotH;

    const column = (x, y) => {
        const h = base - y;
        const r = Math.min(4, barW / 2, h);
        return `M${x},${base} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${base} Z`;
    };
    const describe = (b) => `${hourLabel(b.hour)} – ${hourLabel((b.hour + 1) % 24)}`;

    return (
        <div className="ad-card td-chart-card">
            <div className="ad-card-header">
                <div className="ad-card-title">
                    <i className="fas fa-chart-column"></i>
                    <h2>Sign-ins — last 24 hours</h2>
                </div>
                <div className="td-chart-tools">
                    <span className="td-chart-total">{total.toLocaleString()} sign-ins</span>
                    <button type="button" className="td-chip" aria-pressed={asTable} onClick={() => setAsTable(t => !t)}>
                        <i className={`fas ${asTable ? 'fa-chart-column' : 'fa-table'}`}></i> {asTable ? 'Chart' : 'Table'}
                    </button>
                </div>
            </div>

            {asTable ? (
                <div className="td-chart-table">
                    <table className="ad-table">
                        <thead><tr><th>Hour</th><th>Sign-ins</th><th>Data</th></tr></thead>
                        <tbody>
                            {buckets.map(b => (
                                <tr key={b.key}><td>{describe(b)}</td><td>{b.sessions}</td><td>{formatBytes(b.data)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="td-chart" ref={wrapRef}>
                    <svg width={width} height={HEIGHT} role="list" aria-label="Sign-ins per hour, last 24 hours">
                        {ticks.map(t => (
                            <g key={t} aria-hidden="true">
                                <line x1={PAD.left} x2={width - PAD.right} y1={yOf(t)} y2={yOf(t)} className="td-grid" />
                                <text x={PAD.left - 8} y={yOf(t)} className="td-axis" textAnchor="end" dominantBaseline="middle">{compact(t)}</text>
                            </g>
                        ))}
                        {buckets.map((b, i) => {
                            const x = PAD.left + i * slot + (slot - barW) / 2;
                            const y = yOf(b.sessions);
                            return (
                                <g
                                    key={b.key}
                                    role="listitem"
                                    tabIndex={0}
                                    aria-label={`${describe(b)}: ${b.sessions} sign-ins, ${formatBytes(b.data)}`}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseLeave={() => setActive(null)}
                                    onFocus={() => setActive(i)}
                                    onBlur={() => setActive(null)}
                                    className="td-col"
                                >
                                    <rect x={PAD.left + i * slot} y={PAD.top} width={slot} height={plotH} fill="transparent" />
                                    {b.sessions > 0 && (
                                        <path d={column(x, y)} fill={BAR} opacity={active === i ? 0.75 : 1} />
                                    )}
                                    {i === peak && b.sessions > 0 && (
                                        <text x={x + barW / 2} y={y - 6} className="td-cap" textAnchor="middle">{b.sessions}</text>
                                    )}
                                    {b.hour % 4 === 0 && (
                                        <text x={x + barW / 2} y={HEIGHT - 8} className="td-axis" textAnchor="middle">{hourLabel(b.hour)}</text>
                                    )}
                                </g>
                            );
                        })}
                        <line x1={PAD.left} x2={width - PAD.right} y1={base} y2={base} className="td-baseline" aria-hidden="true" />
                    </svg>

                    {total === 0 && <div className="td-chart-empty">No sign-ins in the last 24 hours</div>}

                    {active !== null && buckets[active] && (
                        <div
                            className="td-tip"
                            role="status"
                            style={{
                                left: PAD.left + active * slot + slot / 2,
                                top: Math.max(0, yOf(buckets[active].sessions)),
                            }}
                        >
                            <strong>{buckets[active].sessions} sign-in{buckets[active].sessions === 1 ? '' : 's'}</strong>
                            <span>{describe(buckets[active])} · {formatBytes(buckets[active].data)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
